import { useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled, { css, useTheme } from 'styled-components'

import { Tooltip, Typography } from '@blade/ui-kit'
import { CurrencyInformationClipboard } from '@root/entities/currency/ui'
import { CurrencyAddressSearchLoupe } from '@root/entities/currency/ui/currency-address-search-loupe'
import { CurrencyInformationSocial } from '@root/entities/currency/ui/currency-information/currency-information-social'
import { Deeplink, DeeplinkURL } from '@root/entities/deeplinking'
import { PairBoostersBadge } from '@root/entities/pair/ui/pair-information/pair-boosters-badge'
import { PairInformationAge } from '@root/entities/pair/ui/pair-information/pair-information-age'
import { PairRank } from '@root/entities/pair/ui/pair-rank'
import { ProviderBadge } from '@root/entities/provider/ui'
import { ShortcutId } from '@root/features/shortcut/shortcut-id'
import { PossibleShortcut } from '@root/features/shortcut/ui/possible-shortcut'
import { useClipboard } from '@root/shared'
import { useWidthThreshold } from '@root/shared/hooks/use-width-threshold'
import {
  CopyButton,
  FadeOverflow,
  mediaMaxDesktopSmall,
  ShareIcon,
  Truncate,
} from '@root/shared/ui-kit'

import { PairCashbackBadge } from '../common/pair-cashback-badge'
import { PairTicker } from '../common/pair-ticker'
import { PairTokenizedAgentBadge } from '../common/pair-tokenized-agent-badge'

import { PairAuditBadge } from './pair-audit-badge'
import { PairInformationDevBurn } from './pair-information-dev-burn'
import { PairInformationDevTokens } from './pair-information-dev-tokens'
import { PairInformationPriceChange } from './pair-information-price-change'

import type { PairTickerProps } from '../common/pair-ticker'
import type { PairModel } from '@root/entities/pair/atoms/reatom-pair'
import type { ClassNameProps } from '@root/shared/react'

export type PairInformationCurrencyProps = {
  pair: PairModel
  withAge?: boolean
  withSocials?: boolean
  withShare?: boolean
  withProvider?: boolean
  withAddress?: boolean
  withBoosters?: boolean
  withDevTokens?: boolean
  withTitleOverflow?: boolean
  withDevBurn?: boolean
  tickerAsLink?: boolean
  withPriceChange?: boolean
  withAudit?: boolean
  onDevTokensClick?: () => void
} & ClassNameProps &
  Pick<PairTickerProps, 'onLinkClick'>

export const PairInformationCurrency: React.FC<
  PairInformationCurrencyProps
> = ({
  pair,
  withAge = false,
  withSocials = false,
  withShare = false,
  withProvider = false,
  withAddress = false,
  withBoosters = false,
  withDevTokens = false,
  withTitleOverflow = false,
  withDevBurn = false,
  withPriceChange = false,
  withAudit = false,
  tickerAsLink,
  className,
  onDevTokensClick,
  onLinkClick,
}) => {
  const theme = useTheme()
  const isOverMinimumWidth = useWidthThreshold(theme.desktopSmall.width)
  const [isCashBackMode] = useAtom(pair.isCashBackMode)
  const [isTokenizedAgent] = useAtom(pair.isTokenizedAgent)
  const { t } = useTranslation()
  const { copyToClipboard } = useClipboard()

  const handleShare = () => {
    const deeplink = new Deeplink.Trade({
      network: pair.network,
      pair: pair.address,
    })
    const url = DeeplinkURL.fromDeeplink(deeplink)
    copyToClipboard(url.toString(), {
      showSnackbar: true,
      snackbarText: t('trading.pair.share-link-copied'),
    })
  }

  const iconSize = isOverMinimumWidth ? 14 : 12

  const [rank] = useAtom(pair.rank)
  const [socials] = useAtom(pair.quote.socials)

  const renderTitle = () => {
    if (!withTitleOverflow) {
      return <TitleLabel ellipsis>{pair.quote.title}</TitleLabel>
    }

    const titleEl = <TitleLabel>{pair.quote.title}</TitleLabel>

    return (
      <FadeOverflow variant="horizontal" inlineStart={0} inlineEnd={10}>
        {({ horizontalOverflow }) =>
          horizontalOverflow ? (
            <Tooltip content={pair.quote.title}>{titleEl}</Tooltip>
          ) : (
            titleEl
          )
        }
      </FadeOverflow>
    )
  }

  return (
    <Root className={className}>
      <Top>
        <Ticker pair={pair} onLinkClick={onLinkClick} asLink={tickerAsLink} />
        <PairRank rank={rank} />
        <PairBoostersBadge
          currency={pair.quote}
          premiumInfo={pair.premiumInfo}
          withBoost={withBoosters}
        />
        <SearchLoupe
          currencyAddress={pair.quote.address}
          symbol={pair.quote.ticker}
          size={iconSize + 2}
        />
        {withSocials ? (
          <PairSocials size={iconSize} socials={socials} />
        ) : undefined}
        {isCashBackMode ? (
          <PairCashbackBadge size={14} onClick={onLinkClick} />
        ) : undefined}
        {isTokenizedAgent ? <PairTokenizedAgentBadge size={14} /> : undefined}
        {withAudit ? <PairAuditBadge pair={pair} /> : undefined}
        {withShare ? (
          <PossibleShortcut shortcutId={ShortcutId.CopyTradeGoto}>
            <Share onClick={handleShare}>
              <Sep />
              <ShareIcon size={iconSize} />
            </Share>
          </PossibleShortcut>
        ) : undefined}
        {withPriceChange ? (
          <PairInformationPriceChange pair={pair} />
        ) : undefined}
      </Top>
      <Bottom>
        <Title>
          {renderTitle()}
          <CurrencyInformationClipboard currency={pair.quote} size={iconSize} />
        </Title>
        {withAddress ? (
          <Address>
            <AddressLabel>
              <Truncate start={5} end={4}>
                {pair.quote.address}
              </Truncate>
            </AddressLabel>
            <CopyButton
              size={iconSize}
              value={pair.quote.address}
              tooltip={t('currency.token_address_copy_tooltip')}
              snackbarText={t('currency.token_address_copied_message', {
                ticker: pair.quote.ticker,
              })}
            />
          </Address>
        ) : undefined}
        {withProvider ? <Provider pair={pair} /> : undefined}
        {withAge ? <PairAge pair={pair} size={iconSize} /> : undefined}
        {withDevBurn ? <PairInformationDevBurn pair={pair} /> : undefined}
        {withDevTokens ? (
          <PairInformationDevTokens pair={pair} onClick={onDevTokensClick} />
        ) : undefined}
      </Bottom>
    </Root>
  )
}
const AddressLabel = styled(Typography)`
  margin-inline-end: 2px;
`
const Address = styled.div`
  display: flex;
  gap: 2px;
`
const Root = styled.div`
  pointer-events: none;
  position: relative;
  display: grid;

  grid-template-rows: auto auto;
  justify-items: start;
  align-items: center;

  gap: var(--pair-ticker-gap, 2px);
  overflow: hidden;
`

const Top = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;

  align-items: center;

  gap: 6px;
`

const Bottom = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  gap: 8px;
  color: var(--text-11);

  justify-items: start;
  align-items: center;

  > svg {
    cursor: pointer;
  }
`

const Ticker = styled(PairTicker)`
  font-size: var(--pair-ticker-font-size, 12px);
  font-weight: 700;
  letter-spacing: 0.48px;
  margin-bottom: -2px;
`

const Title = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 1px;
`

const TitleLabel = styled(Typography)`
  color: var(--text-11);
  cursor: pointer;
  line-height: 16px;
  display: block;
  margin-inline-end: 2px;
  white-space: nowrap;
`

const Share = styled.span`
  color: var(--text-11);
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  gap: 4px;
  pointer-events: auto;

  font-size: 12px;
  font-weight: 400;
  line-height: 16px;

  font-style: normal;
  align-items: center;
  cursor: pointer;

  &:hover {
    color: var(--text-12);
  }

  transition: color 0.2s ease-in-out;

  ${mediaMaxDesktopSmall(css`
    gap: 2px;
    font-size: 11px;
  `)}
`

const Sep = styled.span`
  border-inline-start: 1px solid var(--background-3);

  border-radius: 2px;
  width: 1px;
  height: 14px;
  margin-inline-end: 4px;

  text-transform: capitalize;
`

const Provider = styled(ProviderBadge)``

const PairAge = styled(PairInformationAge)``

const PairSocials = styled(CurrencyInformationSocial)``

const SearchLoupe = styled(CurrencyAddressSearchLoupe)`
  width: 14px;
  height: 16px;
  padding: 0;
`
